import type {
  Account,
  GcpDiskSnapshotProperties,
  Snapshot,
} from '@repo/api-gw-sdk';
import { Provider } from '@repo/api-gw-sdk';

import { GcpDiskConfiguration } from './steps/gcp/gcpDiskConfiguration';
import { SelectTargetAccountStep } from './steps/selectTargetAccount';

import type { Step } from '../wizard/Step';
import { Wizard } from '../wizard/Wizard';

export interface RestoreGcpDiskState {
  resourceId: string;
  snapshot: Snapshot;
  supportedCloudProviders: Provider[];
  restoreAccount?: Account;
  originalDiskSettings: GcpDiskSnapshotProperties;
  regionName?: string;
  originalTags: Record<string, string>;
  zone?: string;
  encryptionKey?: string;
  machineType?: string;
}

const restoreGcpDiskFlow: Step<RestoreGcpDiskState> = {
  id: 'select_target_account_step',
  Component: SelectTargetAccountStep,
  next: [
    {
      id: 'gcp_disk_configuration',
      Component: GcpDiskConfiguration,
    },
  ],
};

export const RestoreGcpDiskWizard = ({
  snapshot,
  disk,
  onClose,
  onBack,
}: {
  snapshot: Snapshot;
  disk?: GcpDiskSnapshotProperties;
  onClose: () => void;
  onBack: () => void;
}) => {
  const machineType = snapshot.resource?.properties?.gcpVm?.machineType
    ?.split('/')
    ?.pop();

  const diskProperties = disk ?? snapshot.resource?.properties?.gcpDisk;
  const innerDisk: GcpDiskSnapshotProperties = {
    diskId: diskProperties?.diskId ?? '',
    diskName: diskProperties?.diskName ?? '',
    diskType: diskProperties?.diskType ?? '',
    diskSizeBytes: diskProperties?.diskSizeBytes ?? 0,
    tags: diskProperties?.tags ?? {},
    iops: diskProperties?.iops,
    throughput: diskProperties?.throughput,
  };

  return (
    <Wizard<RestoreGcpDiskState>
      onAbort={(origin) => (origin === 'back' ? onBack() : onClose())}
      onFinish={async () => true}
      flow={[restoreGcpDiskFlow]}
      initialState={{
        snapshot,
        resourceId: snapshot.resourceId,
        supportedCloudProviders: [Provider.Gcp],
        originalDiskSettings: {
          ...innerDisk,
          diskName: `${innerDisk.diskName ?? ''}-restored`,
        },
        originalTags: { ...innerDisk.tags },
        machineType,
      }}
      title={`Restore disk`}
      stepperLabels={['Restore account', 'Disk configuration']}
    />
  );
};
